/**
 * CalendarEventCard
 * Visual representation of an Event (attendance/appointment)
 * Distinct from TaskCard to avoid confusion
 */

import React from 'react'
import { Gavel, Users, Calendar, MapPin, Clock, Video } from 'lucide-react'
import type { CalendarItem } from '../../types/calendar'
import { CopyableProcessNumber } from '../ui/CopyableProcessNumber'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { CalendarPriorityFlag, eventStatusLabels } from './CalendarBadges'

interface CalendarEventCardProps {
  item: CalendarItem
  onClick?: () => void
  className?: string
}

/**
 * Get event type icon
 */
const getEventIcon = (eventType?: string) => {
  switch (eventType) {
    case 'AUDIENCIA':
      return <Gavel className="size-4" />
    case 'REUNIAO':
      return <Users className="size-4" />
    case 'COMPROMISSO':
      return <Calendar className="size-4" />
    default:
      return <Calendar className="size-4" />
  }
}

/**
 * Borda sólida à esquerda = EVENTO; a cor vem do tema (cancelado/concluído
 * ficam esmaecidos, audiência ganha o destaque primário).
 */
const getEventToneClasses = (eventType?: string, status?: string) => {
  if (status === 'CANCELLED') return 'border-l-border bg-muted/60 text-muted-foreground'
  if (status === 'COMPLETED') return 'border-l-border bg-card text-muted-foreground'
  if (eventType === 'AUDIENCIA') return 'border-l-primary bg-card text-foreground'
  return 'border-l-foreground/40 bg-card text-foreground'
}

/**
 * CalendarEventCard Component
 * ⭐ Solid border + colored background = EVENT (attendance)
 */
export const CalendarEventCard: React.FC<CalendarEventCardProps> = ({
  item,
  onClick,
  className = ''
}) => {
  const toneClasses = getEventToneClasses(item.eventType, item.eventStatus)
  const icon = getEventIcon(item.eventType)

  return (
    <div
      onClick={onClick}
      className={cn(
        'min-w-0 cursor-pointer rounded-lg border border-l-4 p-2 transition-shadow hover:shadow-sm',
        toneClasses,
        className
      )}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="shrink-0 text-muted-foreground">{icon}</span>
          <span className="truncate text-sm font-medium">{item.title}</span>
        </div>
        <CalendarPriorityFlag priority={item.priority} />
      </div>

      {item.startTime && (
        <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="size-3 shrink-0" />
          <span>
            {item.startTime}
            {item.endTime && ` - ${item.endTime}`}
          </span>
        </div>
      )}

      {item.allDay && (
        <div className="mb-1 text-xs font-medium text-muted-foreground">🕐 Dia inteiro</div>
      )}

      {item.rawEvent?.location && (
        <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
          {item.rawEvent?.locationType === 'VIRTUAL' ? (
            <Video className="size-3 shrink-0" />
          ) : (
            <MapPin className="size-3 shrink-0" />
          )}
          <span className="truncate">{item.rawEvent.location}</span>
        </div>
      )}

      {(item.clientName || item.caseNumber) && (
        <div className="mt-1 border-t pt-1">
          <div className="truncate text-[11px] text-muted-foreground">
            {item.clientName && <span>{item.clientName}</span>}
            {item.clientName && item.caseNumber && <span className="mx-1">•</span>}
            {item.caseNumber && <CopyableProcessNumber number={item.caseNumber} className="font-mono" />}
          </div>
        </div>
      )}

      {item.eventStatus && item.eventStatus !== 'SCHEDULED' && (
        <div className="mt-1">
          <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
            {eventStatusLabels[item.eventStatus] ?? item.eventStatus}
          </Badge>
        </div>
      )}
    </div>
  )
}
